import React, { useState } from 'react';
import { Box, IconButton } from '@mui/material';
import { Add as AddIcon, ContentCopy, Delete as DeleteIcon } from '@mui/icons-material';
import { FootNoteProps } from '../models/TopLevelTemplateComponentProps';
import { newFootNote } from '../services/TemplateUtils';
import { StyledButton } from './styles/StyledButtons';
import { StyledInput } from './styles/StyledInputs';
import { StyledTypography } from './styles/styles';
import { Palette } from 'Palette';
import Done from '@mui/icons-material/Done';

interface FootnoteEditorProps {
  footnotes?: FootNoteProps[];
  tone?: keyof typeof Palette;
  onUpdate: (footnotes: FootNoteProps[]) => void;
  className?: string;
}

const FootnoteEditor: React.FC<FootnoteEditorProps> = ({
  footnotes = [],
  tone = 'default',
  onUpdate,
  className,
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [copyingId, setCopyingId] = useState<string | null>(null);

  const handleAddFootnote = () => {
    const nextNumber = Math.max(0, ...footnotes.map((fn) => fn.number)) + 1;
    const newFootnote = newFootNote(nextNumber, '');
    onUpdate([...footnotes, newFootnote]);
    setEditingId(newFootnote.id);
  };

  // A footnote left blank is dropped rather than kept as an empty entry.
  const handleUpdateFootnote = (id: string, content: string) => {
    const updated = content.trim()
      ? footnotes.map((fn) => (fn.id === id ? { ...fn, content } : fn))
      : footnotes.filter((fn) => fn.id !== id);
    onUpdate(updated);
    setEditingId(null);
  };

  const handleDeleteFootnote = (id: string) => {
    const updated = footnotes.filter((fn) => fn.id !== id);
    onUpdate(updated);
  };

  // Clicking an icon would blur the open field, and a blank footnote is dropped on blur —
  // keep focus where it is so copying the reference doesn't delete a footnote still being typed.
  const keepEditingFocus = (event: React.MouseEvent) => event.preventDefault();

  const sortedFootnotes = [...footnotes].sort((a, b) => a.number - b.number);

  const handleCopyFootnoteReference = (footnote: FootNoteProps) => {
    window.navigator.clipboard.writeText('[footnote:' + footnote.number + ']');
    setCopyingId(footnote.id);

    new Promise((resolve) => {
      setTimeout(resolve, 2000);
    }).then(() => {
      setCopyingId(null);
    });
  };

  return (
    <Box
      className={`footnote-editor ${className}`.trim()}
      sx={{
        mt: 2,
        px: 2,
        pb: 2,
        pt: 1.5,
        border: `1px solid ${Palette[tone].border}`,
        borderRadius: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <StyledTypography variant="h6" tone={tone} sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
          Footnotes
        </StyledTypography>
        <StyledButton noTopSpacing size="small" onClick={handleAddFootnote} startIcon={<AddIcon />}>
          Add Footnote
        </StyledButton>
      </Box>

      {sortedFootnotes.length === 0 ? (
        <StyledTypography variant="body2" tone={tone} sx={{ fontStyle: 'italic' }}>
          No footnotes added. Click "Add Footnote" to create one.
        </StyledTypography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {sortedFootnotes.map((footnote) => (
            <Box key={footnote.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <StyledTypography sx={{ minWidth: '2rem', fontWeight: 'bold' }} tone={tone}>
                {footnote.number}.
              </StyledTypography>

              {editingId === footnote.id ? (
                <Box sx={{ flex: 1, display: 'flex', gap: 1 }}>
                  <StyledInput
                    fullWidth
                    multiline
                    minRows={1}
                    placeholder="Type the footnote…"
                    defaultValue={footnote.content}
                    onBlur={(e) => handleUpdateFootnote(footnote.id, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.ctrlKey) {
                        handleUpdateFootnote(footnote.id, (e.target as HTMLInputElement).value);
                      }
                      if (e.key === 'Escape') {
                        if (footnote.content.trim()) setEditingId(null);
                        else handleDeleteFootnote(footnote.id);
                      }
                    }}
                    autoFocus
                  />
                </Box>
              ) : (
                <Box sx={{ flex: 1, cursor: 'pointer' }} onClick={() => setEditingId(footnote.id)}>
                  <StyledTypography tone={tone}>{footnote.content}</StyledTypography>
                </Box>
              )}

              <IconButton
                size="small"
                onMouseDown={keepEditingFocus}
                onClick={() => handleCopyFootnoteReference(footnote)}
                sx={{ color: Palette[tone].text }}
              >
                {copyingId === footnote.id ? (
                  <Done fontSize="small" />
                ) : (
                  <ContentCopy fontSize="small" />
                )}
              </IconButton>

              <IconButton
                size="small"
                onMouseDown={keepEditingFocus}
                onClick={() => handleDeleteFootnote(footnote.id)}
                sx={{ color: Palette[tone].text }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      <Box
        sx={{
          mt: 2,
          p: 1,
          backgroundColor: Palette[tone].activeBg,
          borderRadius: 1,
        }}
      >
        <StyledTypography variant="caption" tone={tone} sx={{ fontSize: '0.875rem' }}>
          Add references in your content using [footnote:1], [footnote:2], etc. The numbers link to
          these footnotes automatically. Use the copy button next to a footnote to copy its
          reference.
        </StyledTypography>
      </Box>
    </Box>
  );
};

export default FootnoteEditor;
